import { formatDate } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges, AfterViewInit, OnDestroy } from '@angular/core';
import { differenceInCalendarDays, sub, add } from 'date-fns';
import { uniq } from 'lodash';
import { SimpleDatatable } from 'src/app/models/SimpleDatatable';
import { SiteRainfallTrendModel } from 'src/app/models/SiteDetails/SiteRainfallTrendModel';
import { googleChartsTooltipHtml, formatTooltipHtml } from '../../shared/google-charts/GoogleChartsTooltipStyles';
import { CHART_DATE_FORMAT, CHART_TOOLTIP_DATE_FORMAT, CHART_TOOLTIP_ENTEROCOCCI_UNITS, CHART_TOOLTIP_RAINFALL_UNITS, DEEP_BLUE_HEX_COLOUR, FORMAT_DATE_LOCALE, TRENDS_TABLE_DATE_FORMAT, TREND_EXPORT_DATA_DATE_FORMAT, TREND_GRAPH_CHART_ANNUAL_DATE_FORMAT } from 'src/app/models/app-constants';
import { Subscription, Observable } from 'rxjs';

import { SitesDetailsService } from 'src/app/services/sitedetails.service';
import { downloadData } from 'src/app/shared/DomUtils';
import { Observe } from 'src/app/shared/custom-decorators/Observe';
import { convertToAESTDateTime, formatDateAEST } from 'src/app/shared/utils';

declare var google : any;


const MEASUREMENT_DATE_FIELD = "MeasurementDt";
const RAINFALL_FIELD = "Rainfall24hr";

@Component({
	selector: 'app-site-rainfall-statistics',
	templateUrl: './site-rainfall-statistics.component.html',
	styleUrls: ['./site-rainfall-statistics.component.scss']
})
export class SiteRainfallStatisticsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() siteRainfallTrends : SiteRainfallTrendModel[];
	@Observe("siteRainfallTrends") private siteRainfallTrends$!: Observable  <SiteRainfallTrendModel[]>;
	@Input() rainGaugeName : string = "";
	@Input() siteName : string;
	@Input() siteId : string = "";
	activeTabIndex : number = 0;	
	
	chartData : any;
	chartOptions : any;
	
	annualChartData : any;
	annualChartOptions : any;
	annualRainfallData :  any;
		
	monthlyChartData : any;
	monthlyChartOptions : any;
	monthlyRainfallData :  any;

	private exportSub : Subscription;
	private dataSub : Subscription;

	private RAINFALL_COLUMN = "Rainfall";
	private RAINFALL_LABEL = `${this.RAINFALL_COLUMN} (${CHART_TOOLTIP_RAINFALL_UNITS})`;
	private MEASUREMENT_DATE_LABEL = "Measurement Date";	


	constructor(private siteDetailsService : SitesDetailsService)
	{

	}


	ngOnInit() : void 
	{		
		this.dataSub = this.siteRainfallTrends$?.subscribe({
			next : (value) => {
				if(Array.isArray(value))
				{
					google?.charts?.load('current', {packages: ['corechart', 'scatter', 'line']});				
					google?.charts?.setOnLoadCallback(this.createChart);					
				}				
			},
			error : (ex : Error) => {

			},
			complete : () => {

			}
		});
	}

	ngOnChanges(changes : SimpleChanges) : void
	{

	}

	ngAfterViewInit(): void {
		
	}

	ngOnDestroy(): void {
		this.exportSub && this.exportSub?.unsubscribe();
		this.dataSub && this.dataSub?.unsubscribe();
	}

	private createChart = () => {			
		if(!google?.visualization?.DataTable || !google?.visualization?.DataView)
		{
			return;
		}
		
		let sortedArray : any[] = [];
		
		try 
		{
			sortedArray = this.siteRainfallTrends?.sort(function compare(a, b) {
				const dateA = new Date(a.MeasurementDt);
				const dateB = new Date(b.MeasurementDt);
				return dateB.getTime() - dateA.getTime();
			}) ?? [];
		}
		catch(ex : any)
		{
			console.log(`[ Exception sorting rainfall data : ${ex?.message} ]`);
		}	


		try
		{
			const maxDate = new Date();
			const minDate = sub(maxDate, {days: 31});
	
			const monthlyData = sortedArray.filter((d) => {
				return d.MeasurementDt.getTime() >= minDate.getTime() &&
					d.MeasurementDt.getTime() <= maxDate.getTime();
			});
					
			this.monthlyChartData = new google.visualization.DataTable();
			
			this.monthlyChartData.addColumn('date', this.MEASUREMENT_DATE_LABEL);
			this.monthlyChartData.addColumn('number', this.RAINFALL_LABEL);
			this.monthlyChartData.addColumn({'type' : 'string', 'role' : 'tooltip', 'p': {'html': true}});
			//this.monthlyChartData.addColumn({'role' : 'style'});
	
			this.monthlyRainfallData = [[this.MEASUREMENT_DATE_LABEL], [this.RAINFALL_LABEL]];
			const diffCalendarDays = differenceInCalendarDays(maxDate, minDate);
			for(let i = 0; i <= diffCalendarDays; ++i)
			{	
				try
				{
					const day = sub(maxDate, {days : i });
					const dayData = monthlyData.find((a : any) => {
						const measurementDt = convertToAESTDateTime(a.MeasurementDt) ?? a.MeasurementDt;					
						const diff = differenceInCalendarDays(day, measurementDt);				
						return 0 === diff;
					});
					if(dayData)
					{
						const measurementDt = convertToAESTDateTime(dayData.MeasurementDt) ?? dayData.MeasurementDt;
						const dt = new Date(measurementDt.getFullYear(), measurementDt.getMonth(), measurementDt.getDate(),
							measurementDt.getHours(), measurementDt.getMinutes(), measurementDt.getSeconds(), measurementDt.getMilliseconds());						
						this.monthlyChartData.addRow([
							dt,
							dayData.Rainfall24hr,
							formatTooltipHtml(
								dayData.Rainfall24hr.toString(),
								CHART_TOOLTIP_RAINFALL_UNITS,
								formatDateAEST(dt,  CHART_TOOLTIP_DATE_FORMAT, FORMAT_DATE_LOCALE),
							)
							//,'opacity: 0.5'
						]);
						this.monthlyRainfallData[0].push(formatDateAEST(dt, TRENDS_TABLE_DATE_FORMAT, FORMAT_DATE_LOCALE));
						this.monthlyRainfallData[1].push(dayData?.Rainfall24hr);
					}
					else
					{
						this.monthlyRainfallData[0].push(formatDateAEST(day, TRENDS_TABLE_DATE_FORMAT, FORMAT_DATE_LOCALE));				
						this.monthlyRainfallData[1].push("");            			
					}	
				}		
				catch(ex : any)
				{
					console.log(`[ Exception parsing rainfall monthly data : ${ex?.message} ]`);
				}		
			}		
			
			const maxYValue = Math.max(...monthlyData.map((a) => a.Rainfall24hr));		
	
			this.monthlyChartOptions = {			
				width: '100%', height: '100%',
				explorer: {
					keepInBounds: true,
					actions : [],
					// actions: ['dragToZoom', 'rightClickToReset'],				
					// axis: 'horizontal',				
					//maxZoomOut: 0.9,
					//maxZoomIn: 0.9	
				},
				// chartArea: {
				//   	width: '95%',
				//   	left: 40,
				//   	right: 40
				// },
				tooltip : { isHtml : true, trigger : 'focus' },			
				hAxis : { 				
					format : TRENDS_TABLE_DATE_FORMAT,
					direction : -1, // most recent to oldest
					gridlines : {
						color: '#fff'
					},
					ticks : [
						maxDate,
						sub(maxDate, {days : 7 }),
						sub(maxDate, {days : 14 }),
						sub(maxDate, {days : 21 }),
						sub(maxDate, {days : 28 })
					]
				},
				series: {
					0: { color: DEEP_BLUE_HEX_COLOUR }
				},
				vAxis : { 
					format : { format : "none" },
					minValue : 0, 
					maxValue : maxYValue,
					// set viewWindow.max to positive value >= in order to show only the positive y axis
					viewWindow: { min: 0, max : maxYValue >= 1 ? maxYValue : 1 },
					minorGridlines : { count : 0 },
					gridlines: { interval: 0.5}
				},
				legend : "none",
				bar: {groupWidth: "95%"}
			}
		}		
		catch(ex : any)
		{
			console.log(`[ Exception creating rainfall monthly chart config : ${ex?.message} ]`);
		}


		try 
		{
			const maxDate = new Date();
			const minDate = sub(maxDate, {years: 1})
			const yearsData = sortedArray.filter((d) => {
				return d.MeasurementDt.getTime() >= minDate.getTime() &&
					d.MeasurementDt.getTime() <= maxDate.getTime();
			});
	
			this.annualChartData = new google.visualization.DataTable();
			this.annualChartData.addColumn('date', this.MEASUREMENT_DATE_LABEL);
			this.annualChartData.addColumn('number', this.RAINFALL_LABEL);			
			this.annualChartData.addColumn({'type' : 'string', 'role' : 'tooltip', 'p': {'html': true}});
			
			this.annualRainfallData = [[this.MEASUREMENT_DATE_LABEL], [this.RAINFALL_LABEL]];
			const diffCalendarDays = differenceInCalendarDays(maxDate, minDate);
			for(let i = 0; i <= diffCalendarDays; ++i)
			{		
				try
				{
					const day = sub(maxDate, { days : i });					
					const dayData =  yearsData.find((a : any) => {
						const measurementDt = convertToAESTDateTime(a.MeasurementDt) ?? a.MeasurementDt;					
						const diff = differenceInCalendarDays(day, measurementDt);				
						return 0 === diff;
					});
					if(dayData)
					{
						const measurementDt = convertToAESTDateTime(dayData.MeasurementDt) ?? dayData.MeasurementDt;
						const dt = new Date(measurementDt.getFullYear(), measurementDt.getMonth(), measurementDt.getDate(),
							measurementDt.getHours(), measurementDt.getMinutes(), measurementDt.getSeconds(), measurementDt.getMilliseconds());
						this.annualChartData.addRow([
							dt,
							dayData.Rainfall24hr,
							formatTooltipHtml(
								dayData.Rainfall24hr.toString(),
								CHART_TOOLTIP_RAINFALL_UNITS,
								formatDateAEST(dt,  CHART_TOOLTIP_DATE_FORMAT, FORMAT_DATE_LOCALE),
							)
						]);
						this.annualRainfallData[0].push(formatDateAEST(dt, TRENDS_TABLE_DATE_FORMAT, FORMAT_DATE_LOCALE));
						this.annualRainfallData[1].push(dayData?.Rainfall24hr);
					}
					else
					{
						this.annualRainfallData[0].push(formatDateAEST(day,TRENDS_TABLE_DATE_FORMAT, FORMAT_DATE_LOCALE));				
						this.annualRainfallData[1].push("");            			
					}			
				}		
				catch(ex : any)
				{
					console.log(`[ Exception parsing rainfall annual data : ${ex?.message} ]`);
				}						
			}		
			
			const maxYValue = Math.max(...yearsData.map((a) => a.Rainfall24hr));
			this.annualChartOptions = {			
				width: '100%', height: '100%',
				explorer: {
					//actions: [],
					keepInBounds: true,									
					axis: 'horizontal',				
					maxZoomOut: 1,
					maxZoomIn: 0.01	
				},
				// chartArea: {
				//   	width: '95%',
				//   	left: 40,
				//   	right: 40
				// },
				tooltip : { isHtml : true, trigger : 'focus' },			
				hAxis : { 				
					format : TRENDS_TABLE_DATE_FORMAT,
					direction : -1, // most recent to oldest
					gridlines : {
						color: '#fff'
					},
					ticks : [
						sub(maxDate, {days: maxDate.getDay()}),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 1 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 2 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 3 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 4 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 5 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 6 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 7 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 8 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 9 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 10 }),
						sub(sub(maxDate, {days: maxDate.getDay()}), {months : 11 })
					]
				},
				vAxis : { 
					format : { format : "none" },
					minValue : 0, 
					maxValue : maxYValue,
					// set viewWindow.max to positive value >= in order to show only the positive y axis
					viewWindow: { min: 0, max : maxYValue >= 1 ? maxYValue : 1 },
					minorGridlines : { count : 0 },
					gridlines: { interval: 0.5}
				},
				series: {
					0: { color: DEEP_BLUE_HEX_COLOUR }
				},
				legend : "none"
			}
		}
		catch(ex : any)
		{
			console.log(`[ Exception creating rainfall annual chart config : ${ex?.message} ]`);
		}
		
	}

	public onExportDataClicked = (type : "Monthly" | "Annual") => {	
		if(!this.siteId)
		{
			return;
		}
		this.exportSub = this.siteDetailsService?.getSiteRainfallTrend(this.siteId, "", "", "", true)?.subscribe({
			next : (response : any) => {
				if(response)
				{
					try
					{
						const jsonResp = JSON.parse(response);
						if(jsonResp?.message)
						{
							alert(jsonResp?.message)
							return;
						}
					}
					catch(ex)
					{

					}					
					downloadData(response, `${type}-Rainfall-${new Date().getTime()?.toString()}`, "text/csv");
				}
			}, 
			error : (ex : Error) => {

			},
			complete : () => {
				
			}
		});
	}	

	/* 
		Since the chart is nested a tab and the inactive tab will be hidden, the chart drawn in inactive tabe will
		not have the width and height of the parent. So defer drawing the chart till the tab is activated
	*/
	onTabActivated = (index : number) => {
		this.activeTabIndex = index;
	}
}
